import React, {
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    ActiveImplantPicker,
    buildWorkflowStateForConfigs,
    EMPTY_WORKFLOW,
} from '@nordinsight/implant-core/ui';
import type {
    WorkflowResult,
    WorkflowState,
} from '@nordinsight/implant-core/ui';
import type {
    ActiveImplantSystem,
    ActiveSystemSummary,
} from '@nordinsight/implant-core/contracts';
import {
    getActivePickerSystem,
    getActivePickerSystems,
} from '../../services/api/activeImplantsService';
import AuthContext from '../../store/auth-context';
import RIGHTS_KEYS from '../../constants/rights-keys';
import LoadingRing from '../ui-components/LoadingRing';
import logoblack from '../../images/Logoblack.png';

/** Host wrapper for the shared active-implant picker: owns workflow state,
 *  routes list/detail through /active-implants[/:id], and fetches from
 *  ni-server's picker endpoints. */
const ActiveImplantsPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Deep link: /active-implants/<id>?configs=<id1,id2> lands directly on
    // that configuration (used by CPT report backlinks).
    const configsParam = searchParams.get('configs');
    // Present when arriving via a CPT report backlink — the picker then
    // offers "Open CPT report" back to that case instead of starting a new one.
    const reportParam = searchParams.get('report');
    const authContext = useContext(AuthContext);
    const canStartCptReport =
        authContext.orRights([RIGHTS_KEYS.cptReporting]) && !reportParam;
    const region = authContext.getContext()?.region;

    const handleOpenCptReport = useCallback(() => {
        if (!reportParam) return;
        navigate(`/manage-reports/${reportParam}?scenario=default`);
        document.getElementById('page-container')?.scrollTo(0, 0);
    }, [navigate, reportParam]);

    const handleStartCptReport = useCallback(
        (result: WorkflowResult) => {
            const params = new URLSearchParams();
            params.set('scenario', 'default');
            params.set('activeSystem', result.system.id);
            if (result.configuration.sourceConfigIds.length) {
                params.set(
                    'activeConfig',
                    result.configuration.sourceConfigIds.join(',')
                );
            }
            navigate(`/manage-reports/new?${params.toString()}`);
            document.getElementById('page-container')?.scrollTo(0, 0);
        },
        [navigate]
    );

    const [workflow, setWorkflow] = useState<WorkflowState>(EMPTY_WORKFLOW);
    const [systems, setSystems] = useState<ActiveSystemSummary[]>();
    const [selectedSystem, setSelectedSystem] = useState<ActiveImplantSystem>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const filesExpireAtRef = useRef<number | null>(null);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            if (id) {
                const { system, filesExpireAt } = await getActivePickerSystem(
                    id
                );
                filesExpireAtRef.current = filesExpireAt;
                setSelectedSystem(system);
                if (configsParam) {
                    const seeded = buildWorkflowStateForConfigs(
                        system,
                        configsParam.split(',')
                    );
                    setWorkflow(seeded ?? EMPTY_WORKFLOW);
                }
            } else {
                setSelectedSystem(undefined);
                setSystems(await getActivePickerSystems());
            }
        } catch (err) {
            console.error('Failed to load active implant systems:', err);
            setError('Could not load active implant systems.');
        } finally {
            setIsLoading(false);
        }
    }, [configsParam, id]);

    useEffect(() => {
        setWorkflow(EMPTY_WORKFLOW);
        fetchData();
    }, [fetchData]);

    // The detail response embeds signed file URLs with a fixed lifetime.
    // Mirroring FileLink's expiry handling: silently refetch shortly before
    // they lapse (and on refocus after a long absence) so the eIFU popup and
    // images keep working however long the page stays open. Workflow state
    // is left untouched — only the URLs change.
    const refreshFileUrls = useCallback(async () => {
        if (!id) return;
        try {
            const { system, filesExpireAt } = await getActivePickerSystem(id);
            filesExpireAtRef.current = filesExpireAt;
            setSelectedSystem(system);
        } catch (err) {
            // Keep the stale URLs; the next timer/focus attempt retries.
            console.error('Failed to refresh file URLs:', err);
        }
    }, [id]);

    useEffect(() => {
        if (!id || !selectedSystem) return;
        const expireAt = filesExpireAtRef.current;
        if (!expireAt) return;

        const REFRESH_MARGIN_MS = 60 * 1000;
        const timer = setTimeout(
            refreshFileUrls,
            Math.max(expireAt - Date.now() - REFRESH_MARGIN_MS, 5_000)
        );
        const onFocus = () => {
            const current = filesExpireAtRef.current;
            if (current && Date.now() > current - REFRESH_MARGIN_MS) {
                refreshFileUrls();
            }
        };
        window.addEventListener('focus', onFocus);
        return () => {
            clearTimeout(timer);
            window.removeEventListener('focus', onFocus);
        };
    }, [id, selectedSystem, refreshFileUrls]);

    if (isLoading) {
        return (
            <div className="flex justify-center p-12">
                <LoadingRing size="large" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 max-w-5xl mx-auto text-center text-red-600">
                {error}
            </div>
        );
    }

    return (
        <div className="p-6">
            <div
                className={`mx-auto w-full ${
                    selectedSystem ? 'max-w-6xl' : 'max-w-3xl'
                }`}
            >
                <ActiveImplantPicker
                    systems={systems}
                    defaultRegion={
                        region === 'US' || region === 'EU' ? region : undefined
                    }
                    selectedSystem={selectedSystem}
                    workflow={workflow}
                    onWorkflowChange={setWorkflow}
                    previewLogo={{ src: logoblack, alt: 'NordInsight' }}
                    onStartCptReport={
                        canStartCptReport ? handleStartCptReport : undefined
                    }
                    onOpenCptReport={
                        reportParam ? handleOpenCptReport : undefined
                    }
                    onSystemSelect={(systemId) => {
                        setWorkflow(EMPTY_WORKFLOW);
                        navigate(
                            systemId
                                ? `/active-implants/${systemId}`
                                : '/active-implants'
                        );
                    }}
                />
            </div>
        </div>
    );
};

export default ActiveImplantsPage;
